import { useState, useEffect, useRef, useCallback } from "react";
import { VideoOff, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { useAuthenticatedVideoUrl } from "@/hooks/useAuthenticatedMedia";
import { cn } from "@/lib/utils";

interface AuthenticatedVideoProps extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'src'> {
  src: string | undefined | null;
  socialControls?: boolean;
}

export function AuthenticatedVideo({
  src,
  className,
  // Extract autoPlay and muted so they are NOT spread onto the DOM element.
  // We handle both solely via the DOM property + programmatic play() call,
  // which is the only reliable approach on iOS Safari.
  autoPlay,
  muted: _muted,
  // Extract socialControls so it is never spread onto the DOM video element.
  socialControls = false,
  // Extract `controls` so we can intentionally suppress it in socialControls
  // mode — native controls must never appear over our custom overlay.
  controls,
  ...props
}: AuthenticatedVideoProps) {
  const resolvedSrc = useAuthenticatedVideoUrl(src ?? undefined);
  const [errored, setErrored] = useState(false);
  // Initialise isMuted to true (matches autoplay-muted policy). We sync from
  // the actual DOM element on canplay so the icon always reflects true state.
  const [isMuted, setIsMuted] = useState(true);
  const [flashIcon, setFlashIcon] = useState<'play' | 'pause' | null>(null);
  const [flashFading, setFlashFading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const flashShowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup flash timers on unmount
  useEffect(() => {
    return () => {
      if (flashShowTimer.current) clearTimeout(flashShowTimer.current);
      if (flashHideTimer.current) clearTimeout(flashHideTimer.current);
    };
  }, []);

  // Reset error state when source changes
  useEffect(() => {
    setErrored(false);
  }, [resolvedSrc]);

  // iOS Safari requires muted to be set as a DOM *property* (not just an HTML attribute)
  // and autoPlay must be triggered via .play() programmatically for reliable playback.
  // We also sync `isMuted` here so the mute icon always reflects the real DOM state.
  const attemptPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    // Sync muted icon from actual DOM state on every canplay / src change.
    setIsMuted(video.muted);
    if (!autoPlay) return;
    video.muted = true;
    setIsMuted(true);
    video.play().catch(() => {
      // Autoplay blocked by browser policy — not a fatal error
    });
  }, [autoPlay]);

  useEffect(() => {
    if (resolvedSrc) {
      attemptPlay();
    }
  }, [resolvedSrc, attemptPlay]);

  // Toggle play/pause and flash the center icon for ~0.8 s
  const handleTap = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const willPlay = video.paused;
    if (willPlay) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }

    // Reset any existing flash timers
    if (flashShowTimer.current) clearTimeout(flashShowTimer.current);
    if (flashHideTimer.current) clearTimeout(flashHideTimer.current);

    // Show icon at full opacity
    setFlashIcon(willPlay ? 'play' : 'pause');
    setFlashFading(false);

    // Start fade-out after 400 ms
    flashShowTimer.current = setTimeout(() => setFlashFading(true), 400);

    // Unmount icon after full 800 ms (400 visible + 400 fade)
    flashHideTimer.current = setTimeout(() => {
      setFlashIcon(null);
      setFlashFading(false);
    }, 800);
  }, []);

  const handleMuteToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Must not trigger play/pause tap handler
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  if (!resolvedSrc) return null;

  if (errored) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground ${className ?? ''}`}
        data-testid="video-error-fallback"
      >
        <VideoOff className="w-8 h-8 opacity-50" />
        <span className="text-xs text-center px-3">Video not supported on this device</span>
      </div>
    );
  }

  if (socialControls) {
    return (
      <div
        className={cn("relative cursor-pointer select-none", className)}
        onClick={handleTap}
        role="button"
        aria-label="Toggle play/pause"
      >
        <video
          ref={videoRef}
          playsInline
          preload="metadata"
          {...props}
          className="w-full h-full object-cover"
          src={resolvedSrc}
          onError={() => setErrored(true)}
          onCanPlay={attemptPlay}
        />

        {/* Center play/pause flash icon */}
        {flashIcon && (
          <div
            className={cn(
              "absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300",
              flashFading ? "opacity-0" : "opacity-100"
            )}
          >
            <div className="bg-black/40 backdrop-blur-sm rounded-full p-4">
              {flashIcon === 'play' ? (
                <Play className="w-10 h-10 text-white fill-white" />
              ) : (
                <Pause className="w-10 h-10 text-white fill-white" />
              )}
            </div>
          </div>
        )}

        {/* Persistent mute/unmute button — bottom-right corner */}
        <button
          onClick={handleMuteToggle}
          className="absolute bottom-2 right-2 flex items-center justify-center w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-white"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? (
            <VolumeX className="w-4 h-4" />
          ) : (
            <Volume2 className="w-4 h-4" />
          )}
        </button>
      </div>
    );
  }

  // Non-social mode: restore `controls` that was extracted from props
  return (
    <video
      ref={videoRef}
      playsInline
      preload="metadata"
      controls={controls}
      {...props}
      className={className}
      src={resolvedSrc}
      onError={() => setErrored(true)}
      onCanPlay={attemptPlay}
    />
  );
}
